import type { OrderItem_V1 } from "@@/shared/orders/order-items/order-item-v1";
import type { OrderItem_V2 } from "@@/shared/orders/order-items/order-item-v2";
import type { Price } from "@@/shared/prices/price";
import { priceZodSchema } from "@@/shared/prices/price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import { emptyArrayOf } from "@@/utilities/empty";
import { z } from "zod";
import type { DiscountCondition } from "./discount-condition";
import type {
    DiscountConditionId} from "./discount-condition-id";
import {
    createDiscountConditionId,
} from "./discount-condition-id";
import type { DiscountConditionValidator } from "./discount-condition-validator";

const valueConditionType = "VALUE_CONDITION";
type ValueConditionType = typeof valueConditionType;

type ValueCondition = {
    _id: DiscountConditionId;
    type: ValueConditionType;
    value: Price;
};

const createValueCondition = (minPrice: Price): ValueCondition => ({
    _id: createDiscountConditionId(),
    type: "VALUE_CONDITION",
    value: minPrice,
});

const getValueConditionValidator =
    <TOrderItem extends OrderItem_V1 | OrderItem_V2 = OrderItem_V2>(
        condition: ValueCondition,
    ): DiscountConditionValidator<TOrderItem> =>
    (input: TOrderItem[]) => {
        const sum = sumPrices(input.map(item => item.price));
        if (sum.amountIncludingVat >= condition.value.amountIncludingVat) {
            return input;
        }
        return emptyArrayOf<TOrderItem>();
    };

const valueConditionZodSchema = z.object({
    _id: z.string() as unknown as z.ZodSchema<DiscountConditionId>,
    type: z.literal("VALUE_CONDITION"),
    value: priceZodSchema,
});

const isValueCondition = (
    condition: DiscountCondition,
): condition is ValueCondition => {
    return condition.type === valueConditionType;
};

export {
    createValueCondition,
    getValueConditionValidator,
    isValueCondition,
    valueConditionType,
    valueConditionZodSchema,
};
export type { ValueCondition, ValueConditionType };
